import React from 'react';
import SEOHelmet from '../components/SEOHelmet';

const TermsOfUsePage = () => {
  const lastUpdated = "October 29, 2025";

  return (
    <>
      <SEOHelmet
        title="Terms of Use - Ceremonzy"
        description="Terms and conditions for using Ceremonzy.com platform. Learn about user responsibilities, content guidelines, and platform policies."
        keywords="terms of use, terms and conditions, ceremonzy policies, user agreement, wedding platform terms"
        url="/terms-of-use"
      />
      <div className="min-h-screen bg-gradient-to-br from-amber-50 via-orange-50 to-red-50">
        <div className="container mx-auto px-4 sm:px-6 lg:px-8 py-8">
          <div className="bg-white rounded-lg shadow-md p-6 sm:p-8 max-w-4xl mx-auto">
            <h1 className="text-3xl font-bold text-gray-800 mb-2">Terms of Use</h1>
            <p className="text-gray-600 mb-8">Last updated: {lastUpdated}</p>

            <div className="prose prose-lg max-w-none">
              
              <section className="mb-8">
                <h2 className="text-2xl font-semibold text-gray-800 mb-4">1. Acceptance of Terms</h2>
                <p className="text-gray-700 mb-4">
                  By accessing and using Ceremonzy.com ("Platform"), you agree to be bound by these Terms of Use 
                  and all applicable laws and regulations. If you do not agree with any of these terms, you are 
                  prohibited from using or accessing this site.
                </p>
                <p className="text-gray-700">
                  Ceremonzy operates as an intermediary platform under the Information Technology Act, 2000 
                  and the Information Technology (Intermediary Guidelines and Digital Media Ethics Code) Rules, 2021.
                </p>
              </section>

              <section className="mb-8">
                <h2 className="text-2xl font-semibold text-gray-800 mb-4">2. Platform Description</h2>
                <p className="text-gray-700 mb-4">
                  Ceremonzy is an online marketplace that connects users with wedding service vendors. 
                  We provide a platform for vendors to showcase their services and for users to discover, 
                  compare, and connect with vendors.
                </p>
                <p className="text-gray-700">
                  <strong>Important:</strong> Ceremonzy acts only as an intermediary and does not provide 
                  wedding services directly. All transactions and service agreements are between users and vendors.
                </p>
              </section>

              <section className="mb-8">
                <h2 className="text-2xl font-semibold text-gray-800 mb-4">3. User-Generated Content Disclaimer</h2>
                <div className="bg-yellow-50 border-l-4 border-yellow-400 p-4 mb-4">
                  <p className="text-yellow-800 font-medium">
                    <strong>Safe Harbor Notice:</strong> All reviews, comments, ratings, and other content 
                    posted by users reflect solely the opinions and experiences of individual users and do not 
                    represent the views, opinions, or endorsements of Ceremonzy.
                  </p>
                </div>
                <ul className="list-disc pl-6 text-gray-700 space-y-2">
                  <li>User reviews and ratings are subjective opinions</li>
                  <li>Ceremonzy does not verify the accuracy of user-submitted content</li>
                  <li>We are not responsible for the quality, accuracy, or reliability of user-generated content</li>
                  <li>Content is provided "as is" without warranties of any kind</li>
                  <li>Users are solely responsible for their posted content</li>
                </ul>
              </section>

              <section className="mb-8">
                <h2 className="text-2xl font-semibold text-gray-800 mb-4">4. Acceptable Use Policy</h2>
                <h3 className="text-xl font-medium text-gray-800 mb-3">Prohibited Content</h3>
                <p className="text-gray-700 mb-3">Users must not post content that is:</p>
                <ul className="list-disc pl-6 text-gray-700 space-y-2 mb-4">
                  <li>Defamatory, obscene, pornographic, or offensive</li>
                  <li>Hateful, discriminatory, or promotes violence</li>
                  <li>Infringes intellectual property rights</li>
                  <li>Contains false or misleading information</li>
                  <li>Violates privacy of individuals</li>
                  <li>Contains spam, advertisements, or promotional content</li>
                  <li>Violates any applicable laws or regulations</li>
                </ul>
                
                <h3 className="text-xl font-medium text-gray-800 mb-3">Prohibited Activities</h3>
                <ul className="list-disc pl-6 text-gray-700 space-y-2">
                  <li>Impersonating other users or entities</li>
                  <li>Posting fake reviews or ratings</li>
                  <li>Attempting to manipulate search rankings</li>
                  <li>Using automated systems to access the platform</li>
                  <li>Attempting to harm platform security or functionality</li>
                </ul>
              </section>

              <section className="mb-8">
                <h2 className="text-2xl font-semibold text-gray-800 mb-4">5. Content Moderation and Removal</h2>
                <p className="text-gray-700 mb-4">
                  While we strive to maintain a safe environment, Ceremonzy does not pre-screen all user content. 
                  However, we reserve the right to:
                </p>
                <ul className="list-disc pl-6 text-gray-700 space-y-2 mb-4">
                  <li>Remove or modify any content that violates these terms</li>
                  <li>Suspend or terminate user accounts for policy violations</li>
                  <li>Cooperate with law enforcement when required</li>
                  <li>Take action upon receiving valid complaints</li>
                </ul>
                <p className="text-gray-700">
                  For content removal requests, please refer to our <a href="/grievance-policy" className="text-pink-600 hover:underline">Grievance Policy</a>.
                </p>
              </section>

              <section className="mb-8">
                <h2 className="text-2xl font-semibold text-gray-800 mb-4">6. Limitation of Liability</h2>
                <div className="bg-red-50 border-l-4 border-red-400 p-4 mb-4">
                  <p className="text-red-800">
                    <strong>Important Disclaimer:</strong> Ceremonzy shall not be liable for any direct, indirect, 
                    incidental, special, or consequential damages arising from the use of this platform or 
                    user-generated content.
                  </p>
                </div>
                <ul className="list-disc pl-6 text-gray-700 space-y-2">
                  <li>We do not guarantee the accuracy or reliability of vendor information</li>
                  <li>We are not responsible for transactions between users and vendors</li>
                  <li>We do not warrant uninterrupted or error-free service</li>
                  <li>Users interact with vendors at their own risk</li>
                  <li>We are not liable for vendor service quality or performance</li>
                </ul>
              </section>

              <section className="mb-8">
                <h2 className="text-2xl font-semibold text-gray-800 mb-4">7. Intellectual Property</h2>
                <p className="text-gray-700 mb-4">
                  The Ceremonzy platform, including its design, code, logos, and original content, is protected 
                  by intellectual property laws. Users retain rights to their submitted content but grant 
                  Ceremonzy a license to use, display, and distribute such content on the platform.
                </p>
              </section>

              <section className="mb-8">
                <h2 className="text-2xl font-semibold text-gray-800 mb-4">8. Privacy and Data Protection</h2>
                <p className="text-gray-700 mb-4">
                  Your privacy is important to us. Please review our <a href="/privacy-policy" className="text-pink-600 hover:underline">Privacy Policy</a> 
                  to understand how we collect, use, and protect your information.
                </p>
              </section>

              <section className="mb-8">
                <h2 className="text-2xl font-semibold text-gray-800 mb-4">9. Termination</h2>
                <p className="text-gray-700 mb-4">
                  We may terminate or suspend your account and access to the platform at our sole discretion, 
                  without prior notice, for conduct that we believe violates these Terms of Use or is harmful 
                  to other users, vendors, or the platform.
                </p>
              </section>

              <section className="mb-8">
                <h2 className="text-2xl font-semibold text-gray-800 mb-4">10. Governing Law</h2>
                <p className="text-gray-700 mb-4">
                  These Terms of Use are governed by the laws of India. Any disputes arising from the use of 
                  this platform shall be subject to the exclusive jurisdiction of the courts in Mumbai, Maharashtra.
                </p>
              </section>

              <section className="mb-8">
                <h2 className="text-2xl font-semibold text-gray-800 mb-4">11. Changes to Terms</h2>
                <p className="text-gray-700 mb-4">
                  We reserve the right to modify these Terms of Use at any time. Changes will be effective 
                  immediately upon posting. Your continued use of the platform constitutes acceptance of 
                  any modifications.
                </p>
              </section>

            </div>
          </div>
        </div>
      </div>
    </>
  );
};

export default TermsOfUsePage;